'use client';

import classnames from 'classnames';
import { ChangeEventHandler, FC, useEffect, useRef } from 'react';

import { IconCircleCheck } from 'src/general/Icons/IconCircleCheck';
import { getInputAria } from '../Input.helpers';
import { InputBaseWithOptions, InputGroupValue, InputOption } from '../Input.type';
import styles from './OptionField.module.scss';
import { Checkbox, Radio, Switch } from './OptionFieldInput';

type Props = Prettify<
  Omit<InputBaseWithOptions<InputGroupValue>, 'options' | 'onChange'> & {
    type: 'radio' | 'checkbox' | 'switch';
    option: InputOption;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onBlur?: () => void;
  }
>;

/** Component representing either a checkbox or a radio */
export const OptionField: FC<Props> = ({
  type,
  option,
  onChange,
  onBlur,
  variant = 'default',
  inputPlacement = 'start',
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const checked = isChecked(option.value, props.value);
  const disabled = option.disabled ?? props.disabled;

  // Handles the indeterminate status of the checkbox
  useEffect(() => {
    if (type === 'checkbox' && inputRef.current && typeof option.indeterminate !== 'undefined') {
      inputRef.current.indeterminate = option.indeterminate;
    }
  }, [option.indeterminate]);

  return (
    <label
      className={classnames(
        styles.root,
        styles[variant],
        styles[type],
        styles[inputPlacement],
        { checked: checked, indeterminate: option.indeterminate, disabled: disabled, error: !!props.error },
        props.inputClassName,
        'labelWrapper',
      )}
      data-testid={props['data-testid'] ? `${props['data-testid']}-${option.value}` : undefined}
    >
      <div className={classnames(styles.inputWrapper, 'inputWrapper')}>
        <input
          type={type === 'switch' ? 'checkbox' : type}
          name={props.name}
          value={option.value}
          disabled={disabled}
          className={styles.input}
          {...getInputAria(props)}
          checked={checked}
          ref={inputRef}
          onChange={onChange}
          onBlur={onBlur}
        />
        {type === 'checkbox' && <Checkbox {...option} error={props.error} checked={checked} disabled={disabled} />}
        {type === 'radio' && (
          <div className={classnames({ [styles.radioChecked]: checked })}>
            <Radio {...option} error={props.error} checked={checked} disabled={disabled} />
            {variant === 'card' && checked && <IconCircleCheck className={styles.radioCheckedCardIcon} />}
          </div>
        )}
        {type === 'switch' && <Switch {...option} error={props.error} checked={checked} disabled={disabled} />}
      </div>
      <div className={classnames(styles.content, 'label')}>{option.label}</div>
    </label>
  );
};

const isChecked = (value: string, inputValue: InputGroupValue | undefined): boolean =>
  (Array.isArray(inputValue) && inputValue.indexOf(value) >= 0) || value === inputValue;
